/**
 * useActionInbox — one fetch of the inbox inputs, shared by the sidebar badge
 * and the /action-inbox page.
 *
 * Module-level cache rather than a context provider: the sidebar mounts on every
 * screen, so without a cache the badge would refetch the dashboard on every
 * navigation. `refreshActionInbox()` clears it after a save, and `seedActionInbox`
 * lets Overview donate the data it already loaded instead of a second request.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { buildActionInbox } from './action-inbox';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

let cache = null;      // { kpis, matrix }
let inflight = null;
const subs = new Set();

const emit = () => subs.forEach((fn) => fn(cache));

export function seedActionInbox(data) {
  cache = data;
  emit();
}

export function refreshActionInbox() {
  cache = null;
  inflight = null;
  emit();
  return load();
}

function load() {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  const y = new Date().getFullYear();
  inflight = Promise.all([
    axios.get(`${API_URL}/api/dashboard?range=last-year`, auth()).then((r) => r.data).catch(() => null),
    axios.get(`${API_URL}/api/emissions/coverage/matrix?year=${y}`, auth()).then((r) => r.data).catch(() => null)
  ])
    .then(([d, m]) => {
      cache = { kpis: d?.kpis || null, matrix: m };
      emit();
      return cache;
    })
    .finally(() => { inflight = null; });

  return inflight;
}

export function useActionInbox({ enabled = true } = {}) {
  const [data, setData] = useState(cache);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!localStorage.getItem('token')) return undefined;
    const fn = (next) => setData(next);
    subs.add(fn);
    load();
    return () => { subs.delete(fn); };
  }, [enabled]);

  const built = data ? buildActionInbox(data) : { tasks: [], counts: { open: 0, gaps: 0, partial: 0 } };
  return { ...built, loading: !data, matrix: data?.matrix || null };
}
